import { Component, type ErrorInfo, type ReactNode } from 'react';

type State = { error: Error | null };

// Last-resort boundary so a render-time throw (bad query data, lazy chunk
// failure, missing branding) shows a usable recovery surface instead of a
// blank document. TanStack Router's per-route errorComponent handles
// route-scoped errors; this catches everything above the router.
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" style={{ padding: 'var(--space-6, 24px)', maxWidth: 640, margin: '4rem auto' }}>
        <h1 style={{ marginTop: 0 }}>Something went wrong.</h1>
        <p>The app hit an unexpected error. You can try again, or reload the page.</p>
        <pre style={{
          background: 'var(--surface-2, #f6f7f9)',
          padding: 'var(--space-3, 12px)',
          borderRadius: 8,
          overflow: 'auto',
          fontSize: 12,
        }}>{this.state.error.message}</pre>
        <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }}>
          <button type="button" onClick={this.reset}>Try again</button>
          <button type="button" onClick={() => location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
}
